import React, { useEffect } from 'react';

import Card from '~/components/Card';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { useSelector, useDispatch } from 'react-redux';
import Spinner from 'react-bootstrap/Spinner';
import { formLabel, titleCase } from '~/utils/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faXmark, faSave } from '@fortawesome/free-solid-svg-icons';
import cloneDeep from 'lodash/cloneDeep';
import { fetchBasicStatApi, edit, cancelEdit, updateBasicStatApi, update } from './basicStatSlice.js';
import ApiValidationErrorDisplay from '~/components/ApiValidationErrorDisplay';
import { clearApiValidationErrors } from '~/stores/redux/common/apiValidationErrorSlice';
import InlineEditingCell from '~/components/Datatable/InlineEditingCell.jsx';

const customStyles = {
    table: {
        style: {
            border: '1px solid #F5F7FA',
        },
    },
    headCells: {
        style: {
            fontWeight: 'bold',
            padding: '1em',
            fontSize: '11px',
        },
    },
};

function BasicStat() {
    const { isLoading } = useSelector((state) => state.loading);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { rows, editedRow } = useSelector((state) => state.heroSaga.basicStat);
    const { apiValidationErrors } = useSelector((state) => state.apiValidationError);

    const handleChange = (e, row, fieldName) => {
        row[fieldName] = parseInt(e.target.value);
        dispatch(update(row));
    };

    const columns = [
        {
            name: 'Type',
            selector: (row) => row.type,
            width: '100px',
            format: (row) => titleCase(row.type),
        },
        {
            name: 'Level',
            selector: (row) => row.basicStatableId,
            width: '60px',
        },
        {
            name: <div>{formLabel('addonAttack')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="addonAttack"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('addonDefense')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="addonDefense"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('addonDv')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="addonDv"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('addonMaxHp')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="addonMaxHp"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('addonHpRegen')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="addonHpRegen"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('dvConditionOption1')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="dvConditionOption1"
                    tag="select"
                />
            ),
            width: '90px',
        },
        {
            name: <div>{formLabel('dvConditionValue1')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="dvConditionValue1"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('dvConditionOption2')}</div>,
            selector: (row) => row.dvConditionOption2,
            width: '90px',
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="dvConditionOption2"
                    tag="select"
                />
            ),
        },
        {
            name: <div>{formLabel('dvConditionValue2')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="dvConditionValue2"
                    tag="input"
                />
            ),
            width: '70px',
        },
        {
            name: <div>{formLabel('feintDvRequirement')}</div>,
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="feintDvRequirement"
                    tag="input"
                />
            ),
            width: '85px',
        },
        {
            name: <div>{formLabel('numberOfFreeReroll')}</div>,
            width: '70px',
            cell: (row) => (
                <InlineEditingCell
                    row={row}
                    editedRow={editedRow}
                    handleChange={handleChange}
                    fieldName="numberOfFreeReroll"
                    tag="input"
                />
            ),
        },
        {
            name: 'Edit',
            cell: (row) => {
                if (row.id !== editedRow.id) {
                    return (
                        <Button
                            size="sm"
                            className={'bg-info border border-info'}
                            onClick={() => {
                                dispatch(edit(row));
                            }}
                        >
                            <FontAwesomeIcon icon={faPencil} />
                        </Button>
                    );
                } else {
                    return (
                        <>
                            <Button
                                size="sm"
                                className={'bg-warning border border-warning mr-2'}
                                onClick={() => {
                                    dispatch(cancelEdit());
                                }}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </Button>
                            <Button
                                size="sm"
                                className={'bg-success border border-success'}
                                onClick={() => {
                                    dispatch(updateBasicStatApi({ row, navigate }));
                                }}
                            >
                                <FontAwesomeIcon icon={faSave} />
                            </Button>
                        </>
                    );
                }
            },
        },
    ];

    useEffect(() => {
        if (rows.length > 0) return;
        dispatch(fetchBasicStatApi(navigate));
    }, []);

    return (
        <Card>
            <ApiValidationErrorDisplay
                errors={apiValidationErrors}
                clearError={clearApiValidationErrors}
            />
            <div className="col-sm-12">
                <DataTable
                    columns={columns}
                    data={cloneDeep(rows)}
                    pagination
                    paginationPerPage="10"
                    striped
                    persistTableHead
                    progressPending={isLoading} //spinning icon will trigger when isLoading = 1
                    noDataComponent={<h3 className="m-3">No data...</h3>}
                    progressComponent={
                        <Spinner
                            animation="border"
                            role="status"
                        >
                            <span className="visually-hidden"></span>
                        </Spinner>
                    }
                    customStyles={customStyles}
                    subHeader
                />
            </div>
        </Card>
    );
}

export default BasicStat;
/**Notes */
//(1) even though this is reset buttton, don't use type="reset" here as it will cause nested form fields (like FormikArray) unable to be reset to its inital values (I debugged and figured it out).
